/**
 * QuickActions - Admin quick action buttons for common tasks
 * With permission-based visibility
 */
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  QrCode, ClipboardList, Mail, Users, Calendar, Gift,
  Ticket, UserPlus, FileText, Mic, Building, Settings, Eye
} from 'lucide-react';
import { useAuthStore } from '@/lib/store';

const QuickActions = ({ eventId, eventSlug, onAction }) => {
  const navigate = useNavigate();
  const { hasAnyPermission, isSuperAdmin } = useAuthStore();

  const allActions = [
    {
      id: 'preview',
      name: 'View as Attendee',
      description: 'Preview delegate experience',
      icon: Eye,
      color: 'bg-amber-500 hover:bg-amber-600',
      onClick: () => navigate(`/admin/event/${eventId}/preview`),
      permissions: [] // Available to all admins
    },
    {
      id: 'kiosk',
      name: 'Unified Kiosk',
      description: 'Multi-mode scanner',
      icon: QrCode,
      color: 'bg-purple-500 hover:bg-purple-600',
      onClick: () => window.open(`/admin/kiosk/${eventId}`, '_blank'),
      permissions: ['delegates.checkin']
    },
    {
      id: 'add-task',
      name: 'Add Task',
      description: 'Create a new task',
      icon: ClipboardList,
      color: 'bg-blue-500 hover:bg-blue-600',
      onClick: () => onAction?.('tasks'),
      permissions: ['tasks.manage']
    },
    {
      id: 'send-email',
      name: 'Send Email',
      description: 'Broadcast to attendees',
      icon: Mail,
      color: 'bg-green-500 hover:bg-green-600',
      onClick: () => onAction?.('comms'),
      permissions: ['communications.send_email']
    },
    {
      id: 'add-delegate',
      name: 'Add Delegate',
      description: 'Register new attendee',
      icon: UserPlus,
      color: 'bg-orange-500 hover:bg-orange-600',
      onClick: () => onAction?.('delegates'),
      permissions: ['delegates.create']
    },
    {
      id: 'add-session',
      name: 'Add Session',
      description: 'Schedule new session',
      icon: Calendar,
      color: 'bg-cyan-500 hover:bg-cyan-600',
      onClick: () => onAction?.('schedule'),
      permissions: ['sessions.create']
    },
    {
      id: 'add-speaker',
      name: 'Add Speaker',
      description: 'Register new speaker',
      icon: Mic,
      color: 'bg-indigo-500 hover:bg-indigo-600',
      onClick: () => onAction?.('speakers'),
      permissions: ['speakers.manage']
    },
    {
      id: 'draw-prize',
      name: 'Draw Prize',
      description: 'Pick raffle winner',
      icon: Gift,
      color: 'bg-pink-500 hover:bg-pink-600',
      onClick: () => onAction?.('prizes'),
      permissions: ['prizes.draw']
    },
    {
      id: 'create-promo',
      name: 'Create Promo',
      description: 'New discount code',
      icon: Ticket,
      color: 'bg-yellow-500 hover:bg-yellow-600',
      onClick: () => onAction?.('promos'),
      permissions: ['tickets.promo_codes']
    },
    {
      id: 'gen-certs',
      name: 'Certificates',
      description: 'Generate certificates',
      icon: FileText,
      color: 'bg-teal-500 hover:bg-teal-600',
      onClick: () => onAction?.('certificates'),
      permissions: ['certificates.issue']
    },
    {
      id: 'venue-map',
      name: 'Venue Map',
      description: 'Edit venue POIs',
      icon: Building,
      color: 'bg-slate-500 hover:bg-slate-600',
      onClick: () => onAction?.('venue'),
      permissions: ['venue.maps']
    }
  ];

  // Filter actions based on permissions
  const actions = useMemo(() => {
    return allActions.filter(action => {
      if (!action.permissions || action.permissions.length === 0) return true;
      if (isSuperAdmin()) return true;
      return hasAnyPermission(action.permissions);
    });
  }, [hasAnyPermission, isSuperAdmin]);

  // Don't render if no actions available
  if (actions.length === 0) return null;

  return (
    <Card data-testid="quick-actions">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Quick Actions
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {actions.map(action => {
            const Icon = action.icon;
            return (
              <Button
                key={action.id}
                variant="outline"
                className={`h-auto py-3 px-3 flex flex-col items-center gap-2 text-white border-0 ${action.color}`}
                onClick={action.onClick}
                data-testid={`quick-action-${action.id}`}
              >
                <Icon className="h-6 w-6" />
                <span className="text-xs font-medium text-center leading-tight">{action.name}</span>
              </Button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default QuickActions;
